import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion, useScroll, useTransform } from 'framer-motion';
import { useSite } from '@/context/SiteContext';
import { mediaUrl } from '@/lib/api';
import { imgProps } from '@/lib/img';
import { cn } from '@/lib/utils';

/** Collect the live photos of a section so Home (and any page block) always mirrors what editors uploaded there. */
export const useLinkedPhotos = (source) => {
  const { site } = useSite();
  return useMemo(() => {
    if (!site || !source) return [];
    const urls = [];
    const push = (u) => { if (u && !urls.includes(u)) urls.push(u); };
    if (source === 'rooms') {
      (site.rooms || []).forEach((r) => {
        (r.photos?.length ? r.photos.map((p) => p.url) : r.images || []).forEach(push);
      });
    } else if (source === 'dining') {
      (site.outlets || []).forEach((o) => {
        (o.images || []).forEach(push);
        (o.food_photos || []).forEach((p) => push(p.url));
      });
    } else if (source === 'experiences') {
      (site.experiences || []).forEach((e) => push(e.image));
    } else if (source === 'gallery') {
      (site.gallery || []).forEach((g) => push(g.url));
    }
    // Gallery-linked media may show up to 50 photos; other sections are capped at 8.
    return urls.slice(0, source === 'gallery' ? 50 : 8);
  }, [site, source]);
};

/**
 * Cross-fading slideshow of linked photos with the same parallax feel as ParallaxImage.
 * Loops forever; with a single photo it simply shows that photo.
 */
export const LinkedMedia = ({ source, fallback = '', alt = '', ratio = 'aspect-[4/3] md:aspect-[5/4]', className, interval = 4500, strength = 40, testId = 'linked-media' }) => {
  const photos = useLinkedPhotos(source);
  const list = photos.length ? photos : fallback ? [fallback] : [];
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'end start'] });
  const y = useTransform(scrollYProgress, [0, 1], [-strength, strength]);
  const [idx, setIdx] = useState(0);

  useEffect(() => { setIdx(0); }, [source, list.length]);
  useEffect(() => {
    if (list.length < 2 || reduce) return undefined;
    const t = setInterval(() => setIdx((i) => (i + 1) % list.length), interval);
    return () => clearInterval(t);
  }, [list.length, interval, reduce]);
  // Preload the next photo so the fade is seamless.
  useEffect(() => {
    if (list.length < 2) return;
    const img = new Image();
    img.src = mediaUrl(list[(idx + 1) % list.length]);
  }, [idx, list]);

  if (!list.length) return null;
  const current = list[Math.min(idx, list.length - 1)];

  return (
    <div ref={ref} className={cn('relative overflow-hidden rounded-xl bg-muted', ratio, className)} data-testid={testId} data-source={source || 'custom'} data-count={list.length}>
      <AnimatePresence initial={false}>
        <motion.img
          key={current}
          {...imgProps(current, { sizes: '(min-width: 768px) 60vw, 100vw' })}
          alt={alt}
          loading="lazy"
          decoding="async"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1.2, ease: 'easeInOut' }}
          style={reduce ? undefined : { y, scale: 1.12 }}
          className="absolute inset-0 h-full w-full object-cover"
          data-testid={`${testId}-image`}
        />
      </AnimatePresence>
      {list.length > 1 && list.length <= 10 && (
        <div className="absolute bottom-3 right-3 flex gap-1.5" data-testid={`${testId}-dots`}>
          {list.map((u, i) => (
            <button key={u} type="button" onClick={() => setIdx(i)} aria-label={`Photo ${i + 1}`} className={`h-1.5 rounded-full transition-[width,background-color] duration-300 ${i === idx ? 'w-5 bg-white' : 'w-1.5 bg-white/50 hover:bg-white/80'}`} />
          ))}
        </div>
      )}
      {list.length > 10 && (
        <span className="absolute bottom-3 right-3 rounded-full bg-black/35 backdrop-blur-[2px] px-2.5 py-1 text-[10px] tracking-[0.18em] text-white/90 tabular-nums" data-testid={`${testId}-counter`}>
          {String(idx + 1).padStart(2, '0')} / {String(list.length).padStart(2, '0')}
        </span>
      )}
    </div>
  );
};
